import { useTranslation } from 'react-i18next';
import './transactions.css';

const PAGE_SIZE_OPTIONS = [10, 15, 25, 50];

interface TransactionsPaginationProps {
  page: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

const TransactionsPagination = ({
  page,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
}: TransactionsPaginationProps) => {
  const { t } = useTranslation();

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const start = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalItems);
  const isFirst = page <= 1;
  const isLast = page >= totalPages;

  return (
    <div className="d-flex align-items-center mt-16">
      <span className="w-100 flex-grow-1 text-dark-grey">
        {t('customertransactions.showing', { start, end, total: totalItems })}
      </span>
      <nav className="w-100 flex-grow-1 d-flex justify-content-center align-items-center gap-16">
        <button
          type="button"
          className="transactions-pagination-arrow text-dark-grey"
          aria-label={t('customertransactions.firstPage') ?? ''}
          disabled={isFirst}
          onClick={() => onPageChange(1)}
        >
          &laquo;
        </button>
        <button
          type="button"
          className="transactions-pagination-arrow text-dark-grey"
          aria-label={t('customertransactions.previousPage') ?? ''}
          disabled={isFirst}
          onClick={() => onPageChange(page - 1)}
        >
          &lsaquo;
        </button>
        <span className="text-nowrap text-dark-grey">
          {t('customertransactions.page', { current: page, total: totalPages })}
        </span>
        <button
          type="button"
          className="transactions-pagination-arrow text-dark-grey"
          aria-label={t('customertransactions.nextPage') ?? ''}
          disabled={isLast}
          onClick={() => onPageChange(page + 1)}
        >
          &rsaquo;
        </button>
        <button
          type="button"
          className="transactions-pagination-arrow text-dark-grey"
          aria-label={t('customertransactions.lastPage') ?? ''}
          disabled={isLast}
          onClick={() => onPageChange(totalPages)}
        >
          &raquo;
        </button>
      </nav>
      <div className="w-100 flex-grow-1 d-flex justify-content-end align-items-center gap-8">
        <label className="mb-0 text-nowrap text-dark-grey" htmlFor="transactionsRowsPerPage">
          {t('customertransactions.rowsPerPage')}
        </label>
        <select
          id="transactionsRowsPerPage"
          className="form-control w-auto min-w-0"
          value={pageSize}
          onChange={(event) => onPageSizeChange(Number(event.target.value))}
        >
          {PAGE_SIZE_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default TransactionsPagination;
